import { Component } from '@theme/component';

/**
 * A custom element that initializes Video.js players inside a video carousel.
 *
 * Reads playback configuration from data attributes on each card element and
 * enforces a "one video plays at a time" rule across the carousel.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} cards - The video card wrappers.
 *
 * @extends Component<Refs>
 */
export class VideoCarouselComponent extends Component {
  /** @type {object[]} */
  #players = [];

  /** @type {ReturnType<typeof setTimeout> | null} */
  #pollTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#waitForVideoJs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#pollTimer) {
      clearTimeout(this.#pollTimer);
      this.#pollTimer = null;
    }

    for (const player of this.#players) {
      try {
        player.dispose();
      } catch (_) {
        // player may already be disposed
      }
    }

    this.#players = [];
  }

  #waitForVideoJs() {
    if (typeof window.videojs !== 'undefined') {
      this.#initPlayers();
      return;
    }

    this.#pollTimer = setTimeout(() => this.#waitForVideoJs(), 150);
  }

  #initPlayers() {
    const cards = this.refs.cards;
    if (!cards || !cards.length) return;

    for (const card of cards) {
      const el = card.querySelector('video.video-js');
      if (!el) continue;

      const cfg = card.dataset;
      const isAutoplay = cfg.autoplay === 'true';
      const isLoop = cfg.loop === 'true';
      const isMuted = cfg.muted === 'true';
      const preload = cfg.preload || 'metadata';
      const hasFullscreen = cfg.fullscreen === 'true';
      const hideDelay = parseInt(cfg.hideControlsDelay, 10) || 3000;
      const showPlaybackRates = cfg.showPlaybackRates === 'true';

      let playbackRates = [0.5, 1, 1.5, 2];
      if (cfg.playbackRates) {
        try {
          playbackRates = cfg.playbackRates
            .split(',')
            .map((r) => parseFloat(r.trim()))
            .filter((r) => !isNaN(r));
        } catch (_) {
          // keep defaults
        }
      }

      const player = window.videojs(el, {
        controls: true,
        autoplay: isAutoplay ? 'muted' : false,
        loop: isLoop,
        muted: isMuted,
        preload,
        fluid: false,
        playsinline: true,
        disablePictureInPicture: true,
        enableSmoothSeeking: true,
        inactivityTimeout: hideDelay,
        playbackRates: showPlaybackRates ? playbackRates : [],
        userActions: { hotkeys: true },
        controlBar: {
          volumePanel: { inline: true },
          pictureInPictureToggle: false,
          fullscreenToggle: hasFullscreen,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true,
          remainingTimeDisplay: false,
          playbackRateMenuButton: showPlaybackRates,
        },
        html5: {
          nativeTextTracks: false,
          vhs: { overrideNative: false },
        },
      });

      player.on('play', () => {
        for (const p of this.#players) {
          if (p !== player && !p.paused()) p.pause();
        }
      });

      this.#players.push(player);
    }
  }
}

if (!customElements.get('video-carousel-component')) {
  customElements.define('video-carousel-component', VideoCarouselComponent);
}
